import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppDefinition, LabelRef } from '@/platform/app/contracts';
import { modulesFor, type Viewer } from '@/platform/auth/audience';
import type { TenkoNavMainSection } from '@/shared/components/layout/TenkoNavMain';
import type { TenkoNavSecondaryItem } from '@/shared/components/layout/TenkoNavSecondary';

const ICON_SIZE = 18;

const byOrder = <T extends { order?: number }>(a: T, b: T) =>
  (a.order ?? 0) - (b.order ?? 0);

/**
 * Derives the sidebar from the modules the viewer's audience mounts: nav contributions grouped into
 * the definition's sections (empty sections are hidden) plus footer items.
 */
export function useShellNav(app: AppDefinition, viewer: Viewer) {
  const { t } = useTranslation();

  return useMemo(() => {
    const resolve = (ref: LabelRef) => t(ref.key, { ns: ref.ns });
    const modules = modulesFor(app, viewer);
    const navItems = modules.flatMap((m) => m.nav ?? []);

    const sections: TenkoNavMainSection[] = app.navSections
      .map((section) => {
        const Icon = section.icon;
        return {
          id: section.id,
          title: resolve(section.title),
          icon: <Icon size={ICON_SIZE} />,
          defaultOpen: section.defaultOpen ?? true,
          items: navItems
            .filter((item) => item.sectionId === section.id)
            .sort(byOrder)
            .map((item) => ({ to: item.to, label: resolve(item.label) })),
        };
      })
      .filter((section) => section.items.length > 0);

    const footerItems: TenkoNavSecondaryItem[] = modules
      .flatMap((m) => m.footerNav ?? [])
      .sort(byOrder)
      .map((item) => {
        const Icon = item.icon;
        return {
          to: item.to,
          label: resolve(item.label),
          icon: <Icon size={ICON_SIZE} />,
        };
      });

    return { sections, footerItems };
  }, [app, viewer, t]);
}
